import type { ScaleTime } from 'd3-scale'
import { useMemo } from 'react'

import {
  AXIS_LINE_COLOR,
  AXIS_TEXT_COLOR,
  MAJOR_TICK_COLOR,
  MAJOR_TICK_HEIGHT,
  MIDNIGHT_MARKER_COLOR,
  MIDNIGHT_MARKER_OPACITY,
  MINOR_TICK_COLOR,
  TICK_FONT_SIZE,
  TICK_FONT_SIZE_SMALL,
} from '@/features/bidding/components/cascading/charts/constants'

/** Minimum spacing between tick labels in pixels */
const MIN_LABEL_SPACING = 45

type TimeAxisProps = {
  scale: ScaleTime<number, number>
  height: number
  width: number
  /** Datetimes for each data point - renders a small tick for each */
  datetimes: Date[]
}

/**
 * Get the local hour and minute from a date.
 */
function getLocalTime(date: Date): { localHours: number; localMinutes: number } {
  return {
    localHours: date.getHours(),
    localMinutes: date.getMinutes(),
  }
}

/**
 * Check if a date is at midnight (00:00)
 */
function isMidnight(date: Date): boolean {
  const { localHours, localMinutes } = getLocalTime(date)
  return localHours === 0 && localMinutes === 0
}

/**
 * Determine the appropriate hour interval for major ticks based on time range duration.
 * Returns the hour interval (e.g., 4 = every 4 hours).
 */
function getMajorTickInterval(startDate: Date, endDate: Date): number {
  const durationHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60)

  if (durationHours <= 24) {
    // 24 hours or less: every 4 hours
    return 4
  } else if (durationHours <= 72) {
    // Up to 3 days: every 12 hours (noon and midnight)
    return 12
  } else if (durationHours <= 168) {
    // Up to 7 days: every 24 hours (midnight only)
    return 24
  } else {
    // Longer ranges: every 48 hours
    return 48
  }
}

/**
 * Check if a tick is a major tick based on the time range and hour interval.
 */
function isMajorTick(date: Date, hourInterval: number): boolean {
  const { localHours, localMinutes } = getLocalTime(date)

  // Only on the hour
  if (localMinutes !== 0) return false

  return localHours % hourInterval === 0
}

/**
 * Format a tick label appropriately based on the time.
 * At midnight: show date (e.g., "Dec 7")
 * Otherwise: show time (e.g., "06:00" or "18:00")
 */
function formatTickLabel(date: Date, locale = 'en'): string {
  const { localHours, localMinutes } = getLocalTime(date)

  if (localHours === 0 && localMinutes === 0) {
    // At midnight: show date
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
    }).format(date)
  }

  // Otherwise: show time in 24h format
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/**
 * X-axis for time data - rendered via React JSX.
 * Renders a small light grey tick for every data point,
 * with larger dark grey ticks and labels at major intervals.
 */
function TimeAxis({ scale, height, width, datetimes }: Readonly<TimeAxisProps>) {
  const [domainStart, domainEnd] = scale.domain()
  const hourInterval = getMajorTickInterval(domainStart, domainEnd)

  // Find midnight datetimes for vertical markers
  const midnightDatetimes = useMemo(() => datetimes.filter(isMidnight), [datetimes])

  // Calculate which datetimes should show labels (major ticks that don't overlap)
  const ticksWithLabels = useMemo(() => {
    const result: Set<number> = new Set()
    let lastLabelX = Number.NEGATIVE_INFINITY

    for (const datetime of datetimes) {
      if (isMajorTick(datetime, hourInterval)) {
        const x = scale(datetime)
        // Check spacing from last label and from edges
        const fromStart = x
        const fromEnd = width - x

        if (x - lastLabelX >= MIN_LABEL_SPACING && fromStart >= 10 && fromEnd >= 10) {
          result.add(datetime.getTime())
          lastLabelX = x
        }
      }
    }

    return result
  }, [datetimes, hourInterval, scale, width])

  // Create set of major tick times for quick lookup
  const majorTickTimes = useMemo(() => {
    const result: Set<number> = new Set()
    for (const datetime of datetimes) {
      if (isMajorTick(datetime, hourInterval)) {
        result.add(datetime.getTime())
      }
    }
    return result
  }, [datetimes, hourInterval])

  const [rangeStart, rangeEnd] = scale.range()

  // Determine font size based on available space
  const fontSize = width < 400 ? TICK_FONT_SIZE_SMALL : TICK_FONT_SIZE

  return (
    <g className="time-axis" transform={`translate(0, ${height})`}>
      {/* Midnight markers - dashed vertical lines extending up through chart */}
      {midnightDatetimes.map((datetime) => (
        <line
          key={`midnight-${datetime.getTime()}`}
          className="midnight-marker"
          opacity={MIDNIGHT_MARKER_OPACITY}
          stroke={MIDNIGHT_MARKER_COLOR}
          strokeDasharray="2,2"
          x1={scale(datetime)}
          x2={scale(datetime)}
          y1={-height}
          y2={0}
        />
      ))}

      {/* Minor ticks - light grey tick for every data point (rendered first, under axis line) */}
      {datetimes.map((datetime) => {
        const tickTime = datetime.getTime()
        const isMajor = majorTickTimes.has(tickTime)

        // Skip if this is a major tick (we render those separately)
        if (isMajor) return null

        return (
          <line
            key={`minor-${tickTime}`}
            stroke={MINOR_TICK_COLOR}
            // strokeLinecap="round"
            transform={`translate(${scale(datetime)}, 0)`}
            y2={MAJOR_TICK_HEIGHT}
          />
        )
      })}

      {/* Major ticks - dark grey ticks with labels (rendered before axis line) */}
      {datetimes.map((datetime) => {
        const tickTime = datetime.getTime()
        const isMajor = majorTickTimes.has(tickTime)
        const showLabel = ticksWithLabels.has(tickTime)

        if (!isMajor) return null

        return (
          <g key={`major-${tickTime}`} className="tick" transform={`translate(${scale(datetime)}, 0)`}>
            {/* Major tick mark - dark grey */}
            <line stroke={MAJOR_TICK_COLOR} y2={MAJOR_TICK_HEIGHT} />
            {/* Time/date label - only on major ticks that pass spacing check */}
            {showLabel && (
              <text dy="0.71em" fill={AXIS_TEXT_COLOR} fontSize={fontSize} textAnchor="middle" x={0} y={9}>
                {formatTickLabel(datetime)}
              </text>
            )}
          </g>
        )
      })}

      {/* Axis line - dark grey (rendered last, on top of ticks) */}
      <path
        d={`M${rangeStart + 0.5},0.5H${rangeEnd + 0.5}`}
        fill="none"
        shapeRendering="auto"
        stroke={AXIS_LINE_COLOR}
      />
    </g>
  )
}

export default TimeAxis
